import type {Router, RouteLocation} from 'src/types/router';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {classify} from 'src/utils/classify';

import {FilterCard} from './';
import Checkbox from 'src/components/lib/checkbox';

import {getMessageTypeGoals} from 'src/components/analytics/v2/message-type-goals';

import css from './message-type-card.css';
import {type TranslationFunction} from 'src/types/language-support';


type Props = {
  onSave?: (e: React.SyntheticEvent) => unknown;
  onCancel?: (e: React.SyntheticEvent) => unknown;
  onChange?: (e: React.SyntheticEvent<HTMLInputElement>) => unknown;
  selectedTypes: string[];
  eventTypeCounts?:
    | {
        [key: string]: number;
      }
    | null
    | undefined;
  eventTypes?: {
    value: string;
    label: string;
  }[];
  history?: Router;
  location?: RouteLocation;
  showSkipped?: boolean;
};

const getMessageTypes = (t: TranslationFunction) => [
  {
    key: 'survey_completion',
    label: t('Surveys', 'Surveys'),
    goal: getMessageTypeGoals(t).survey,
  },
  {
    key: 'chatbot_completion',
    label: t('Chatbots', 'Chatbots'),
    goal: getMessageTypeGoals(t).chatbot,
  },
  {
    key: 'email_link_click',
    label: t('Emails_with_links', 'Emails with links'),
    goal: getMessageTypeGoals(t).email_with_links,
  },
  {
    key: 'email_open',
    label: t('Emails_without_links', 'Emails without links'),
    goal: getMessageTypeGoals(t).email_no_links,
  },
  {
    key: 'sms_reply',
    label: t('SMS', 'SMS'),
    goal: getMessageTypeGoals(t).sms,
  },
];

const getMessageTypesSkipped = (t: TranslationFunction) => [
  {
    key: 'survey_completion',
    label: t('Skipped_Surveys', 'Skipped Surveys'),
    goal: getMessageTypeGoals(t).survey,
  },
  {
    key: 'chatbot_completion',
    label: t('Skipped_Chatbots', 'Skipped Chatbots'),
    goal: getMessageTypeGoals(t).chatbot,
  },
  {
    key: 'email',
    label: t('Skipped_Emails', 'Skipped Emails'),
    goal: getMessageTypeGoals(t).email,
  },
  {
    key: 'sms_reply',
    label: t('Skipped_SMS', 'Skipped SMS'),
    goal: getMessageTypeGoals(t).sms,
  },
];

const MessageTypeCardV2 = ({
  onSave,
  onCancel,
  onChange,
  selectedTypes,
  showSkipped,
}: Props) => {
  const {t} = useLabelTranslation();
  const MessageTypes = getMessageTypes(t);
  const MessageTypesSkipped = getMessageTypesSkipped(t);
  return (
    <FilterCard onSave={onSave} onCancel={onCancel}>
      <div className={css.container}>
        <div className={css.header}>
          <span>{t('Message_Type', 'Message Type')}</span>
          <span>{t('Message_Goal', 'Message Goal')}</span>
        </div>
        <ul className={css.checklist}>
          {(showSkipped ? MessageTypesSkipped : MessageTypes).map(
            ({key, label, goal}) => (
              <li key={key}>
                <div className={css.filterItemRow}>
                  <label
                    className={classify(css.label, {
                      [css.labelChecked]: selectedTypes.includes(key),
                    })}
                  >
                    <Checkbox
                      className={css.checkbox}
                      name={key}
                      checked={selectedTypes.includes(key)}
                      onChange={onChange}
                    />
                    {label}
                  </label>
                  <label
                    className={classify(css.label, {
                      [css.labelChecked]: selectedTypes.includes(key),
                    })}
                  >
                    {goal}
                  </label>
                </div>
              </li>
            ),
          )}
        </ul>
      </div>
    </FilterCard>
  );
};

export default MessageTypeCardV2;
